import { useCallback } from 'react';
import { format } from 'date-fns';
import usePDA1040_R01Store from './usePDA1040_R01Store';
import {
  getPurchaseOrderList,
  getPurchaseOrderHeader,
  getPurchaseOrderDetails,
  scanBarcode,
  registerTransport,
} from 'src/apis/purchaseOrderApi';
import { useToast } from 'src/hooks/useToast';
import {
  PDA1040_R01DetailDto,
  PDA1040_R01HeaderDto,
} from 'src/containers/PDA/PDA1040_R01/PDA1040_R01Dto';

/** 발주 상세 기준 미입고량 (발주량 - 기존 입고량) */
const getRemainQty = (detail: PDA1040_R01DetailDto): number =>
  detail.remainQty ?? detail.orderQty - (detail.transportQty ?? 0);

const PDA1040_R01Content = () => {
  const {
    setLoading,
    searchCondition,
    setOrderList,
    detailList,
    selectedOrder,
    setSelectedOrder,
    setDetailList,
    barcode,
    scannedItem,
    setScannedItem,
    transportQty,
    warehouseCd,
    setWarehouseCd,
    warehouseNm,
    setWarehouseNm,
    remark,
    checkedList,
    setCheckedList,
    addCheckedItem,
    clearCheckedList,
    resetScanForm,
  } = usePDA1040_R01Store();

  const { success, error, warning } = useToast();

  /**
   * 발주 목록 조회
   */
  const handleSearchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getPurchaseOrderList(searchCondition);

      if (response.data.successFlag && response.data.data) {
        setOrderList(response.data.data);
        success(`${response.data.data.length}건의 발주를 조회했습니다.`);
      } else {
        setOrderList([]);
        warning('조회된 발주가 없습니다.');
      }
    } catch (err: any) {
      console.error('발주 목록 조회 실패:', err);
      error(err?.response?.data?.message || '발주 목록 조회 중 오류가 발생했습니다.');
      setOrderList([]);
    } finally {
      setLoading(false);
    }
  }, [searchCondition, setLoading, setOrderList, success, warning, error]);

  /**
   * 발주 선택 (좌측 그리드 클릭)
   */
  const handleSelectOrder = useCallback(
    async (order: PDA1040_R01HeaderDto) => {
      if (selectedOrder?.purchaseOrderNo === order.purchaseOrderNo) {
        return; // 이미 선택된 발주
      }

      setLoading(true);
      try {
        // 발주 헤더 조회
        const headerResponse = await getPurchaseOrderHeader(order.purchaseOrderNo);
        
        if (!headerResponse.data.successFlag || !headerResponse.data.data) {
          throw new Error('발주 헤더 조회 실패');
        }

        setSelectedOrder(headerResponse.data.data);

        // 발주 상세 조회
        const detailResponse = await getPurchaseOrderDetails(order.purchaseOrderNo);
        
        if (detailResponse.data.successFlag && detailResponse.data.data) {
          // 배열인지 확인
          const details = Array.isArray(detailResponse.data.data) 
            ? detailResponse.data.data
            : [];
          setDetailList(details);
        } else {
          setDetailList([]);
        }

        // 스캔 폼 초기화
        resetScanForm();
        
        // 검수 목록 초기화 (뒤로 가기 시)
        clearCheckedList();
      } catch (err: any) {
        console.error('발주 조회 실패:', err);
        error(err?.response?.data?.message || '발주 조회 중 오류가 발생했습니다.');
        setSelectedOrder(null);
        setDetailList([]);
      } finally {
        setLoading(false);
      }
    },
    [selectedOrder, setLoading, setSelectedOrder, setDetailList, resetScanForm, error]
  );

  /**
   * 바코드 스캔
   */
  const handleScanBarcode = useCallback(async (): Promise<boolean> => {
    if (!selectedOrder) {
      warning('발주를 먼저 선택하세요.');
      return false;
    }

    if (!barcode || barcode.trim() === '') {
      warning('바코드를 입력하세요.');
      return false;
    }

    setLoading(true);
    try {
      const response = await scanBarcode(barcode.trim(), selectedOrder.purchaseOrderNo);

      // dev 환경에서 인터셉터가 에러를 처리한 경우 response가 없을 수 있음
      if (!response || !response.data) {
        setScannedItem(null);
        return false;
      }

      if (response.data.successFlag && response.data.data) {
        const item = response.data.data;
        setScannedItem(item);

        // 창고가 비어있을 때만 품목의 창고 정보를 자동으로 세팅
        // (사용자가 이미 창고를 선택한 경우 유지)
        if (!warehouseCd || warehouseCd.trim() === '') {
          const detailItem = detailList.find((d) => d.itemCd === item.itemCd);
          if (detailItem && detailItem.warehouseCd) {
            setWarehouseCd(detailItem.warehouseCd);
            if (detailItem.warehouseNm) {
              setWarehouseNm(detailItem.warehouseNm);
            }
          }
        }

        if (!item.existsInOrder) {
          warning('해당 품목은 발주에 포함되어 있지 않습니다.');
        } else {
          success('품목 정보를 조회했습니다.');
        }
        return true;
      } else {
        // successFlag가 false인 경우 (정상 응답이지만 실패)
        setScannedItem(null);
        return false;
      }
    } catch (err: any) {
      // 에러 발생 시 (dev 환경에서는 인터셉터가 이미 에러 표시)
      console.error('바코드 스캔 실패:', err);
      setScannedItem(null);
      return false;
    } finally {
      setLoading(false);
    }
  }, [
    selectedOrder,
    barcode,
    warehouseCd,
    detailList,
    setLoading,
    setScannedItem,
    setWarehouseCd,
    setWarehouseNm,
    success,
    warning,
    error,
  ]);

  /**
   * 검수 (로컬에 임시 저장)
   */
  const handleCheckTransport = useCallback((): boolean => {
    if (!selectedOrder) {
      warning('발주를 먼저 선택하세요.');
      return false;
    }

    if (!barcode || barcode.trim() === '') {
      warning('바코드를 입력하세요.');
      return false;
    }

    if (!scannedItem) {
      warning('바코드를 먼저 스캔하세요.');
      return false;
    }

    if (!transportQty || transportQty <= 0) {
      warning('입고수량을 입력하세요.');
      return false;
    }

    if (!warehouseCd || warehouseCd.trim() === '') {
      warning('창고를 선택하세요.');
      return false;
    }

    const detail = detailList.find((d) => d.itemCd === scannedItem.itemCd);
    if (!detail) {
      warning('해당 품목이 발주 상세에 없습니다.');
      return false;
    }

    const alreadyChecked = checkedList.find(
      (item) => item.itemCd === scannedItem.itemCd
    );
    const remainQty = getRemainQty(detail);
    const newTotalQty = (alreadyChecked?.checkQty ?? 0) + transportQty;

    if (newTotalQty > remainQty) {
      warning(
        `입고 가능 수량을 초과했습니다.\n${scannedItem.itemNm}: 가능 ${remainQty.toLocaleString()}, 요청 ${newTotalQty.toLocaleString()}`
      );
      return false;
    }

    if (alreadyChecked) {
      const updated = checkedList.map((item) =>
        item.itemCd === scannedItem.itemCd
          ? { ...item, checkQty: item.checkQty + transportQty }
          : item
      );
      setCheckedList(updated);
      success(
        `수량이 증가되었습니다: ${scannedItem.itemNm} (${alreadyChecked.checkQty} → ${alreadyChecked.checkQty + transportQty})`
      );
      resetScanForm();
      return true;
    }

    const checkedItem = {
      id: `${Date.now()}-${scannedItem.itemCd}`,
      itemCd: scannedItem.itemCd,
      itemNm: scannedItem.itemNm,
      barcode: barcode.trim(),
      checkQty: transportQty,
      warehouseCd: warehouseCd.trim(),
      warehouseNm: warehouseNm || '',
      remark: remark.trim(),
    };

    addCheckedItem(checkedItem);
    success(`검수 완료: ${scannedItem.itemNm} / 수량: ${transportQty}`);
    resetScanForm();
    return true;
  }, [
    selectedOrder,
    barcode,
    scannedItem,
    transportQty,
    warehouseCd,
    warehouseNm,
    remark,
    detailList,
    checkedList,
    setCheckedList,
    addCheckedItem,
    resetScanForm,
    success,
    warning,
  ]);

  /**
   * 입고 완료 (검수 목록 일괄 처리)
   * - 테이블 순서(itemSeq)대로 정렬 후 itemList 하나로 묶어 1회 호출
   * - 서버 단일 트랜잭션: 1 요청 = 1 매입전표 (전체 성공 or 전체 롤백)
   */
  const handleCompleteTransport = useCallback(async () => {
    if (!selectedOrder) {
      warning('발주를 먼저 선택하세요.');
      return;
    }

    if (checkedList.length === 0) {
      warning('검수된 항목이 없습니다.');
      return;
    }

    setLoading(true);
    try {
      // 테이블 순서(itemSeq)대로 정렬 (LOT/전표 라인 순서 보장)
      const detailMap = new Map(detailList.map(d => [d.itemCd, d.itemSeq]));
      const sortedCheckedList = [...checkedList].sort((a, b) => {
        const seqA = detailMap.get(a.itemCd) || 999;
        const seqB = detailMap.get(b.itemCd) || 999;
        return seqA - seqB;
      });

      // 검수 목록 → itemList 하나로 묶기
      const requestData = {
        purchaseOrderNo: selectedOrder.purchaseOrderNo,
        logicCenter: selectedOrder.logicCenter || '',
        transportDt: format(new Date(), 'yyyy-MM-dd'),
        remark: '',
        itemList: sortedCheckedList.map((item) => ({
          barcode: item.barcode,
          itemCd: item.itemCd,
          transportQty: item.checkQty,
          warehouseCd: item.warehouseCd,
          itemStatus: 'A',
          remark: item.remark || '',
        })),
      };

      const result = await registerTransport(requestData);

      if (result.data.successFlag) {
        // 전체 성공 (단일 트랜잭션)
        success(`${sortedCheckedList.length}건의 입고가 완료되었습니다.`);

        // 발주 마감 여부
        if (result.data.data?.orderClosed) {
          warning('모든 품목 입고가 완료되어 발주가 마감되었습니다.');
        }

        // 발주 상세 재조회
        const detailResponse = await getPurchaseOrderDetails(
          selectedOrder.purchaseOrderNo
        );
        if (detailResponse.data.successFlag && detailResponse.data.data) {
          const details = Array.isArray(detailResponse.data.data)
            ? detailResponse.data.data
            : [];
          setDetailList(details);
        }

        // 검수 목록 및 스캔 폼 초기화
        clearCheckedList();
        resetScanForm();
      } else {
        // 서버가 successFlag=false로 응답 (전체 롤백됨, checkedList 유지)
        error(result.data.message || '입고 완료 중 오류가 발생했습니다.');
      }
    } catch (err: any) {
      // 예외 발생 시에도 전체 롤백. checkedList는 유지되어 재시도 가능.
      console.error('입고 완료 실패:', err);
      error(err?.response?.data?.message || '입고 완료 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }, [
    selectedOrder,
    checkedList,
    detailList,
    setLoading,
    setDetailList,
    clearCheckedList,
    resetScanForm,
    success,
    warning,
    error,
  ]);

  return {
    handleSearchOrders,
    handleSelectOrder,
    handleScanBarcode,
    handleCheckTransport,
    handleCompleteTransport,
  };
};

export default PDA1040_R01Content;
